import type { RatingStatistics } from './rating-statistics';
import { isRatingStatistics } from './rating-statistics';
import type { RatingPolarity } from './questionnaire';

export type ChartSegmentSlideType =
  | "single-select-bars"
  | "multi-select-bars"
  | "ranking"
  | "rating-likert"
  | "matrix-rating"
  | "matrix-categorical-bars"
  | "continuous-density"
  | "quadrant-map";

export interface ChartSegmentIdLabel {
  id: string;
  label: string;
  metricId?: string;
  cellId?: string;
  unit?: string;
  /** Null when the measure declares no bound (Python `Optional[float]`). */
  min?: number | null;
  max?: number | null;
}

export interface ChartSegmentAxis {
  id: string;
  label: string;
  kind: "exclusive" | "overlapping";
  levels: Array<ChartSegmentIdLabel>;
}

export interface ChartSegmentMeta {
  axes: Array<ChartSegmentAxis>;
  tagsRevision: string;
  /**
   * Question and slide ids for open ends that have qualitative analysis but no
   * quantitative metric. Served decks omit trace fields; the gear uses this list.
   */
  openEndSegmentBlockedIds?: Array<string>;
  /**
   * True when the demographic axes could not be loaded, as opposed to this study
   * genuinely having none. An empty `axes` is otherwise indistinguishable from a
   * failure, which silently removes the control instead of reporting a problem.
   */
  axesUnavailable?: boolean;
}

export interface ChartSegmentViewRequest {
  /** Generation timestamp of the displayed report; cuts must belong to that revision. */
  reportRevision?: string;
  /** Absent for saved legacy slides. Never silently upgrade their statistics. */
  ratingSemanticsVersion?: 'rating-statistics.v1';
  axisId: string;
  levelIds?: Array<string>;
  slideType: ChartSegmentSlideType;
  metricId?: string;
  questionId: string;
  /** Per-lane slide identity. Absent on pooled/historical slides. */
  assetId?: string;
  columns: Array<ChartSegmentIdLabel>;
  rows?: Array<ChartSegmentIdLabel>;
  categories?: Array<ChartSegmentIdLabel>;
  /** The viewer's UI language for the plot chrome (`view.plot.data.labels`). */
  locale?: string;
}

export interface ChartSegmentHeatmapRow {
  id: string;
  label: string;
  n: number;
  thin?: boolean;
  values: Array<number | null>;
}

export interface ChartSegmentHeatmapView {
  kind: "heatmap";
  format: "pct" | "score";
  overlapAllowed: boolean;
  scale: [string, string];
  columns: Array<ChartSegmentIdLabel>;
  rows: Array<ChartSegmentHeatmapRow>;
  overflowNote?: string;
  emptyReason?: string;
}

export interface ChartSegmentLikertBucket {
  id: string;
  label: string;
  value: number;
  count?: number | null;
  rawMin?: number | null;
  rawMax?: number | null;
}

/**
 * One bar segment exactly as the python builder stamped it. Mirrors
 * `$defs.RatingSegment` in
 * `packages/slide-schemas/presets/rating-likert.schema.json`, so the drilldown
 * and the slide draw the same bar. Renderers draw `band` as given and never
 * derive or regroup bands.
 */
export interface ChartSegmentLikertSegment {
  label: string;
  value: number;
  band: "favorable" | "middle" | "unfavorable" | "mixed";
}

export interface ChartSegmentLikertDivergingView {
  ratingStatistics?: RatingStatistics | null;
  kind: "likert-diverging";
  buckets: Array<ChartSegmentLikertBucket>;
  rows?: Array<{
    id: string;
    label: string;
    buckets: Array<ChartSegmentLikertBucket>;
    segments?: Array<ChartSegmentLikertSegment> | null;
    ratingStatistics?: RatingStatistics | null;
  }>;
  /**
   * Optional: a cached drilldown response computed before this field existed
   * carries no segments, and the renderer keeps the legacy three-band bar.
   */
  segments?: Array<ChartSegmentLikertSegment> | null;
  scale: {
    min: number;
    max: number;
    minLabel?: string | null;
    maxLabel?: string | null;
    polarity?: RatingPolarity;
    kind?: "rating" | "nps";
  };
  mean?: number | null;
  topBoxPercent?: number | null;
}

export interface ChartSegmentMatrixRatingView {
  kind: "matrix-rating";
  measures: Array<{ id: string; label: string; average?: number | null; unit?: string; min?: number | null; max?: number | null; polarity?: RatingPolarity }>;
  rows: Array<{
    id: string;
    label: string;
    average?: number | null;
    cells: Array<{
      measureId: string;
      value: number | null;
      normalizedValue: number | null;
    }>;
  }>;
  scale: { min: number; max: number } | null;
}

export interface ChartSegmentQuadrantPoint {
  id: string;
  label: string;
  /** `null` when the segment has no score for this asset — not an origin point. */
  x: number | null;
  y: number | null;
  quadrant?: "star" | "question" | "niche" | "risk";
}

export interface ChartSegmentQuadrantView {
  kind: "quadrant";
  axisLabels: { x: string; y: string };
  midpoints: { x: number; y: number };
  points: Array<ChartSegmentQuadrantPoint>;
}

export interface ChartSegmentMatrixCategoricalView {
  kind: "matrix-categorical";
  categories: Array<ChartSegmentIdLabel>;
  rows: Array<{
    id: string;
    label: string;
    /** `null` when the row has no answered base for this cut — not a zero. */
    bars: Array<{ categoryId: string; label: string; value: number | null }>;
  }>;
}

export type ChartSegmentInnerView =
  | ChartSegmentHeatmapView
  | ChartSegmentLikertDivergingView
  | ChartSegmentMatrixRatingView
  | ChartSegmentMatrixCategoricalView
  | ChartSegmentQuadrantView;

export interface ChartSegmentTab {
  id: string;
  label: string;
  n: number;
  thin?: boolean;
  view: ChartSegmentInnerView;
}

export interface ChartSegmentTabbedView {
  kind: "tabbed";
  overlapAllowed: boolean;
  tabs: Array<ChartSegmentTab>;
  overflowNote?: string;
  emptyReason?: string;
}

export type ChartSegmentView = ChartSegmentHeatmapView | ChartSegmentTabbedView;

function likertRowRatingSemanticsMatch(
  view: ChartSegmentLikertDivergingView,
  policy: RatingStatistics['policyVersion'],
): boolean {
  const rows = view.rows;
  if (!rows?.length) {
    return true;
  }
  return rows.every((row) => {
    if (!row.ratingStatistics) {
      return true;
    }
    return isRatingStatistics(row.ratingStatistics)
      && row.ratingStatistics.policyVersion === policy;
  });
}

/** A negotiated calculation policy may never silently fall back to legacy data. */
export function hasRequestedRatingSemantics(view: ChartSegmentView, policy?: RatingStatistics['policyVersion']): boolean {
  if (!policy) return true;
  return view?.kind === 'tabbed' && Array.isArray(view.tabs) && view.tabs.every(
    (tab) => tab?.view?.kind === 'likert-diverging'
      && isRatingStatistics(tab.view.ratingStatistics)
      && tab.view.ratingStatistics.policyVersion === policy
      && likertRowRatingSemanticsMatch(tab.view, policy),
  );
}
